import {HttpClient, HttpHeaders, HttpParams} from '@angular/common/http';
import {Injectable} from '@angular/core';
import {BehaviorSubject, catchError, map, Observable, of, switchMap, take, tap, throwError, timeout} from 'rxjs';
import {
  GlossaryEntries,
  GlossaryEntry,
  Response,
  ServiceResponse,
  StatDataDTO,
  StatItemDisplay,
  StatsDisplay,
  TopTenDataDTO, TopTenItem
} from 'src/app/model/types';
import {v4 as uuidv4} from 'uuid';
import {ConfigService} from '../config/config.service';

@Injectable()
export class ApiService {

  public conf: any;
  private standardTimeout = 5000;
  private timeoutConfig = {
    each: this.standardTimeout,
    with: () => throwError(() => new Error('Errore connessione backend: i servizi non hanno risposto in tempo.'))
  };
  private genericError = (err: any) => throwError(() => new Error('Errore backend: si è verificato un errore inatteso con i servizi. ' + err.message));


  constructor(private http: HttpClient, private config: ConfigService) {
    this.conf = this.config.getConfig();
  }

  _setHeaders() {
    const uuid = uuidv4();
    return new HttpHeaders().set('Correlation-Id', uuid);
  }

  /**
   * Valida i parametri di ricerca.
   * Per essere valida, una ricerca deve avere almeno una query o almeno un filtro
   * Se esiste il solo parametro "page", la ricerca non potrà essere effettuata.
   * @param query
   * @private
   */
  private validateSearchParams(query: object) {
    return Object.keys(query).length >= 2;
  }

  public cerca(queryParams: any): Observable<any> {

    if (!this.validateSearchParams(queryParams)) {
      return of({data: {content: []}});
    }

    const url = `${this.conf.baseUrl}formadosaggio/ricerca`;

    let params = new HttpParams();

    // Costruisco i query params dall'oggetto in arrivo
    for (const key in queryParams) {
      if (Object.prototype.hasOwnProperty.call(queryParams, key)) {
        params = params.set(key, queryParams[key]);
      }
    }

    return this.http.get(url, {params: params, headers: this._setHeaders()})
      .pipe(
        catchError(this.genericError),
        timeout(this.timeoutConfig)
      );
  }

  public medicinaleById(id: string): Observable<any> {
    const url = `${this.conf.baseUrl}formadosaggio/${id}`;
    return this.http.get(url, {headers: this._setHeaders()}).pipe(
      catchError(this.genericError),
      timeout(this.timeoutConfig)
    );
  }

  public medicinaleByIdLang(id: string, lang: string): Observable<any> {
    const url = `${this.conf.baseUrl}formadosaggio/${id}?lang=${lang}`;
    return this.http.get(url, {headers: this._setHeaders()}).pipe(
      catchError(this.genericError),
      timeout(this.timeoutConfig)
    );
  }

  public download(sisCode: string | null, aic6: string | null, tipoStampato: string | null) {
    //TODO null check ed errore
    const url = `${this.conf.baseUrl}organizzazione/${sisCode}/farmaci/${aic6}/stampati?ts=${tipoStampato}`;
    return this.http.get(url, {headers: this._setHeaders().set('Accept', 'application/octet-stream'), responseType: 'blob', observe: 'response'}).pipe(
      catchError(this.genericError),
      timeout(this.timeoutConfig)
    );
  }

  public statistics() {
    const url = `${this.conf.baseUrlCached}statistiche/`;
    return this.http.get<ServiceResponse<StatDataDTO[]>>(url, {headers: this._setHeaders()})
      .pipe(
        catchError(this.genericError),
        timeout(this.timeoutConfig),
        take(1),
        map(data => data.data),
        switchMap(statsData => {
          return of(this.statsDataTransform(statsData));
        })
      );
  }

  public topTenData() {
    const url = `${this.conf.baseUrlCached}statistiche/ultimi`;

    return this.http.get<ServiceResponse<TopTenDataDTO>>(url, {headers: this._setHeaders()})
      .pipe(
        catchError(this.genericError),
        timeout(this.timeoutConfig),
        take(1),
        map(data => {
          return this.topTenDataTransform(data.data);
        })
      );
  }

  private topTenDataTransform(topTenData: TopTenDataDTO): TopTenDataDTO {
    const confezioni = topTenData.confezioni;
    const medicinali = topTenData.medicinali;

    const returnObject = new TopTenDataDTO();

    returnObject.confezioni = confezioni.map(this.mapDate);

    returnObject.medicinali = medicinali.map(this.mapDate);

    return returnObject;
  }

  private mapDate(item: TopTenItem): TopTenItem {
    const formattedData = new Date(item.data);
    formattedData.setDate(formattedData.getDate() - 1);
    return {...item, data: formattedData};
  }

  public suggestion(query: string) {
    const url = `${this.conf.baseUrl}autocomplete?query=${query}&nos=${this.conf.numberOfSuggestion}`;
    return this.http.get(url, {headers: this._setHeaders()}).pipe(
      catchError(this.genericError),
      timeout(this.timeoutConfig)
    );
  }

  public getGlossaryEntries(lang:string): Observable<GlossaryEntries> {
    const url = `${this.conf.baseUrlCached}glossario?lan=${lang}`;
    return this.http.get<ServiceResponse<GlossaryEntries>>(url, {headers: this._setHeaders()})
      .pipe(
        catchError(this.genericError),
        timeout(this.timeoutConfig),
        map(serviceResponse => serviceResponse.data),
      );
  }

  public getGlossaryEntry(character: string,lang:string): Observable<GlossaryEntry[]> {
    return this.getGlossaryEntries(lang).pipe(
      map(glossaryData => glossaryData[character] || [])
    );
  }

  private statsDataTransform(statsData: StatDataDTO[]): StatsDisplay {
    if (!statsData) {
      return {};
    }

    const transformedStatsData: StatsDisplay = {};

    statsData.forEach(stat => {
      this.transform(stat, transformedStatsData);
    });

    return transformedStatsData;
  }

  private transform(stat: StatDataDTO, transformedStatsData: StatsDisplay) {
    let statDisplayItem = new StatItemDisplay('', '', undefined, 0, 0);
    const titleMedAllopatici = $localize`:search-page|stat-title-allopatici@@search-page.stat-title-allopatici:MEDICINALI ALLOPATICI AUTORIZZATI (*)`;
    const titleMedOmeopatici = $localize`:search-page|stat-title-omeopatici@@search-page.stat-title-omeopatici:MEDICINALI OMEOPATICI AUTORIZZATI (*)`;
    const titleConfAllopatici = $localize`:search-page|stat-title-ConfAllopatici@@search-page.stat-title-ConfAllopatici:CONFEZIONI DI MEDICINALI ALLOPATICI AUTORIZZATE`;
    const titleConfOmeopatici = $localize`:search-page|stat-title-ConfOmeopatici@@search-page.stat-title-ConfOmeopatici:CONFEZIONI DI MEDICINALI OMEOPATICI AUTORIZZATE`;
    const titlePrincipiAttivi = $localize`:search-page|stat-title-principiattivi@@search-page.stat-title-principiattivi:PRINCIPI ATTIVI`;
    // const titleCarenti = $localize`:search-page|stat-title-carenti@@search-page.stat-title-carenti:CARENTI`;
    const titleOrfani = $localize`:search-page|stat-title-orfani@@search-page.stat-title-orfani:ORFANI`;


    // Caso medicinali allopatici
    if (stat.descrizione.includes('medicinali.allopatici')) {

      if (transformedStatsData['medicinali.allopatici']) {
        statDisplayItem = transformedStatsData['medicinali.allopatici'];
      }

      statDisplayItem.iconName = 'shield-done';
      statDisplayItem.title = titleMedAllopatici;
      statDisplayItem.link = stat.link;

      if (stat.descrizione.includes('autorizzati')) {
        statDisplayItem.authorizedValue = stat.valore;
      }

      if (stat.descrizione.includes('incommercio')) {
        statDisplayItem.onTheMarketValue = stat.valore;
      }

      transformedStatsData['medicinali.allopatici'] = {
        ...transformedStatsData['medicinali.allopatici'],
        ...statDisplayItem
      };
    }

    // Caso medicinali omeopatici
    if (stat.descrizione.includes('medicinali.omeopatici')) {

      if (transformedStatsData['medicinali.omeopatici']) {
        statDisplayItem = transformedStatsData['medicinali.omeopatici'];
      }

      statDisplayItem.iconName = 'shield-d-icon';
      statDisplayItem.title = titleMedOmeopatici;
      statDisplayItem.link = stat.link;

      if (stat.descrizione.includes('autorizzati')) {
        statDisplayItem.authorizedValue = stat.valore;
      }

      if (stat.descrizione.includes('incommercio')) {
        statDisplayItem.onTheMarketValue = stat.valore;
      }

      transformedStatsData['medicinali.omeopatici'] = {
        ...transformedStatsData['medicinali.omeopatici'],
        ...statDisplayItem
      };
    }

    // Caso confezioni allopatiche
    if (stat.descrizione.includes('confezioni.allopatiche')) {

      if (transformedStatsData['confezioni.allopatiche']) {
        statDisplayItem = transformedStatsData['confezioni.allopatiche'];
      }

      statDisplayItem.iconName = 'box-icon';
      statDisplayItem.title = titleConfAllopatici;
      statDisplayItem.link = stat.link;

      if (stat.descrizione.includes('autorizzate')) {
        statDisplayItem.authorizedValue = stat.valore;
      }

      if (stat.descrizione.includes('incommercio')) {
        statDisplayItem.onTheMarketValue = stat.valore;
      }

      transformedStatsData['confezioni.allopatiche'] = {
        ...transformedStatsData['confezioni.allopatiche'],
        ...statDisplayItem
      };
    }

    // Caso confezioni OMEOPATICHE
    if (stat.descrizione.includes('confezioni.omeopatiche')) {

      if (transformedStatsData['confezioni.omeopatiche']) {
        statDisplayItem = transformedStatsData['confezioni.omeopatiche'];
      }

      statDisplayItem.iconName = 'box-d-icon';
      statDisplayItem.title = titleConfOmeopatici;
      statDisplayItem.link = stat.link;

      if (stat.descrizione.includes('autorizzate')) {
        statDisplayItem.authorizedValue = stat.valore;
      }

      if (stat.descrizione.includes('incommercio')) {
        statDisplayItem.onTheMarketValue = stat.valore;
      }

      transformedStatsData['confezioni.omeopatiche'] = {
        ...transformedStatsData['confezioni.omeopatiche'],
        ...statDisplayItem
      };
    }

    /*     // Caso Medicinali
        if (stat.property === 'statistica.nome.medicinali.totali') {
          statDisplayItem.iconName = 'shield-done';
          statDisplayItem.title = 'MEDICINALI TOTALI';
          statDisplayItem.link = stat.link;
          statDisplayItem.authorizedValue = stat.valore;

          transformedStatsData['medicinali.totali'] = {
            ...statDisplayItem
          };
        } */

    // Caso Principi attivi
    if (stat.property === 'statistica.nome.principi.attivi.totali') {
      statDisplayItem.iconName = 'pills-icon';
      statDisplayItem.title = titlePrincipiAttivi;
      statDisplayItem.link = stat.link;
      statDisplayItem.authorizedValue = stat.valore;

      transformedStatsData['principi.attivi.totali'] = {
        ...statDisplayItem
      };
    }

    /*     // Caso Confezioni
        if (stat.property === 'statistica.nome.confezioni.totali') {
          statDisplayItem.iconName = 'shield-done';
          statDisplayItem.title = 'CONFEZIONI';
          statDisplayItem.link = stat.link;
          statDisplayItem.authorizedValue = stat.valore;

          transformedStatsData['confezioni.totali'] = {
            ...statDisplayItem
          };
        }

        // Caso Innovativi
        if (stat.property === 'statistica.nome.medicinali.innovativo') {
          statDisplayItem.iconName = 'shield-done';
          statDisplayItem.title = 'INNOVATIVI';
          statDisplayItem.link = stat.link;
          statDisplayItem.authorizedValue = stat.valore;

          transformedStatsData['medicinali.innovativo'] = {
            ...statDisplayItem
          };
        } */

    // Caso Carenti
    // if (stat.property === 'statistica.nome.medicinali.carente') {
    //   statDisplayItem.iconName = 'def-icon';
    //   statDisplayItem.title = titleCarenti;
    //   statDisplayItem.link = stat.link;
    //   statDisplayItem.authorizedValue = stat.valore;

    //   transformedStatsData['medicinali.carente'] = {
    //     ...statDisplayItem
    //   };
    // }

    // Caso Orfani
    if (stat.property === 'statistica.nome.medicinali.orfani') {
      statDisplayItem.iconName = 'dna-icon';
      statDisplayItem.title = titleOrfani;
      statDisplayItem.link = stat.link;
      statDisplayItem.authorizedValue = stat.valore;

      transformedStatsData['medicinali.orfani'] = {
        ...statDisplayItem
      };
    }

  }

  getFarmacoDetails(id: string): Observable<Response> {
    return this.http.get<Response>(`${this.conf.baseUrl}formadosaggio/${id}?lang=it`).pipe(
      catchError(this.genericError),
      timeout(this.timeoutConfig)
    );
  }
}

